import { StoreStatus } from "../../services/generated/waba-api/models/store-status";
import { WorkHour } from "../../services/generated/waba-api/models/work-hour";
import { APP_PATHS } from "../../shared/utils/app-route-paths";

/**
 * What the back office knows about a restaurant, turned into warnings, each with one button that fixes it. A null value means it could not be read: no sentence and
 * no warning is made from it, so a failed call never shows a false alarm.
 */
export interface RestaurantFacts {
  dishes: number | null;
  salePoints: number | null;
  /** Active sales points with a position on the map. */
  placedSalePoints: number | null;
  /** At least one active delivery method with a zone. */
  zonesReady: boolean | null;
  tables: number | null;
  hours: WorkHour[] | null;
  store: StoreStatus | null;
  /** At least one active payment method. */
  paymentReady: boolean | null;
}

/** REOPEN and OPEN_HOURS are done in place; a path is a page to open. */
export type RestaurantFix = { action: "REOPEN" } | { action: "OPEN_HOURS" } | { action: "NAVIGATE"; path: string };

export interface RestaurantWarning {
  id: string;
  severity: "danger" | "warn";
  messageKey: string;
  fixKey: string;
  fix: RestaurantFix;
  /** Pages (url prefixes) where the warning is also shown; the dashboard shows them all. */
  pages: string[];
}

const DASHBOARD = "/dashboard";

export function restaurantWarnings(facts: RestaurantFacts): RestaurantWarning[] {
  const warnings: RestaurantWarning[] = [];
  if (facts.store?.manualClosed && facts.store.open === false) {
    warnings.push({
      id: "closed",
      severity: "danger",
      messageKey: "RESTAURANT_HEALTH.WARN.CLOSED",
      fixKey: "RESTAURANT_HEALTH.FIX.REOPEN",
      fix: { action: "REOPEN" },
      pages: [DASHBOARD, APP_PATHS.catalog.orders],
    });
  }
  if (facts.dishes === 0) {
    warnings.push({
      id: "no-dishes",
      severity: "danger",
      messageKey: "RESTAURANT_HEALTH.WARN.NO_DISHES",
      fixKey: "RESTAURANT_HEALTH.FIX.ADD_DISH",
      fix: { action: "NAVIGATE", path: APP_PATHS.catalog.productNew },
      pages: [DASHBOARD, APP_PATHS.catalog.products],
    });
  }
  if (facts.hours && !facts.hours.length) {
    warnings.push({
      id: "no-hours",
      severity: "warn",
      messageKey: "RESTAURANT_HEALTH.WARN.NO_HOURS",
      fixKey: "RESTAURANT_HEALTH.FIX.SET_HOURS",
      fix: { action: "OPEN_HOURS" },
      pages: [DASHBOARD, APP_PATHS.catalog.orders],
    });
  }
  if (facts.placedSalePoints === 0) {
    warnings.push({
      id: "no-map",
      severity: "warn",
      messageKey: "RESTAURANT_HEALTH.WARN.NO_MAP",
      fixKey: "RESTAURANT_HEALTH.FIX.PLACE_ON_MAP",
      fix: { action: "NAVIGATE", path: `${APP_PATHS.catalog.whatsappStorefront}?tab=salePoints` },
      pages: [DASHBOARD, APP_PATHS.catalog.whatsappStorefront, APP_PATHS.catalog.orders],
    });
  }
  if (facts.paymentReady === false) {
    warnings.push({
      id: "no-payment",
      severity: "danger",
      messageKey: "RESTAURANT_HEALTH.WARN.NO_PAYMENT",
      fixKey: "RESTAURANT_HEALTH.FIX.ADD_PAYMENT",
      fix: { action: "NAVIGATE", path: `${APP_PATHS.catalog.orders}?panel=payment` },
      pages: [DASHBOARD, APP_PATHS.catalog.orders],
    });
  }
  if (facts.zonesReady === false) {
    warnings.push({
      id: "no-zones",
      severity: "warn",
      messageKey: "RESTAURANT_HEALTH.WARN.NO_ZONES",
      fixKey: "RESTAURANT_HEALTH.FIX.ADD_ZONES",
      fix: { action: "NAVIGATE", path: `${APP_PATHS.catalog.orders}?panel=delivery` },
      pages: [DASHBOARD, APP_PATHS.catalog.orders],
    });
  }
  return warnings;
}

/** The warnings to show on a page: all of them on the dashboard, the related ones elsewhere. */
export function warningsForUrl(warnings: RestaurantWarning[], url: string): RestaurantWarning[] {
  const path = url.split(/[?#]/)[0];
  return warnings.filter((warning) => warning.pages.some((page) => path === page || path.startsWith(page + "/")));
}
